import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { RING_PATH } from '../home/v3/LogoMark';
import { prefersReduced, smooth } from '../home/v3/motion';

/*
 * Portal: a transição da Idealizer.
 * Um círculo escuro se abre a partir do ponto do clique e cobre a tela,
 * o anel do logo se desenha no centro, a troca (rota ou posição na página)
 * acontece por baixo, e o anel cresce e se dissolve: a pessoa "atravessa" o anel.
 * Assim nenhuma navegação mostra um scroll gigante ou um corte seco.
 */

const api = { run: null };

/** Executa `action` escondida atrás do portal. `from` = evento ou {x, y}. */
export function portal(action, from) {
  if (api.run) api.run(action, from);
  else action();
}

/** Leva até um elemento da mesma página através do portal (sem scroll visível). */
export function portalToElement(selector, from, offset = 104) {
  const el = typeof selector === 'string' ? document.querySelector(selector) : selector;
  if (!el) return;
  portal(() => {
    // Desconta o header fixo para o título do destino ficar à vista.
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    if (smooth.lenis) smooth.lenis.scrollTo(top, { immediate: true, force: true });
    else window.scrollTo(0, top);
  }, from);
}

function pointOf(from) {
  if (from && typeof from.clientX === 'number' && (from.clientX || from.clientY)) {
    return { x: from.clientX, y: from.clientY };
  }
  if (from && from.currentTarget && from.currentTarget.getBoundingClientRect) {
    const r = from.currentTarget.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }
  if (from && typeof from.x === 'number') return from;
  return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}

export default function Portal() {
  const overlay = useRef(null);
  const ring = useRef(null);
  const busy = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    const el = overlay.current;
    const svg = ring.current;
    const path = svg.querySelector('path');
    const len = path.getTotalLength();

    api.run = (action, from) => {
      if (busy.current) return;
      if (prefersReduced()) {
        action();
        return;
      }
      busy.current = true;
      const { x, y } = pointOf(from);
      const far = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y)) + 40;

      gsap.killTweensOf([el, svg, path]);
      gsap.set(el, { visibility: 'visible', opacity: 1, clipPath: `circle(0px at ${x}px ${y}px)` });
      gsap.set(svg, { scale: 0.86, rotate: -40, opacity: 1 });
      gsap.set(path, { strokeDasharray: len, strokeDashoffset: len });

      gsap
        .timeline({
          onComplete: () => {
            gsap.set(el, { visibility: 'hidden', clipPath: 'none' });
            busy.current = false;
          },
        })
        .to(el, { clipPath: `circle(${far}px at ${x}px ${y}px)`, duration: 0.55, ease: 'power3.inOut' })
        .to(path, { strokeDashoffset: 0, duration: 0.5, ease: 'power2.inOut' }, 0.22)
        .to(svg, { scale: 1, rotate: 0, duration: 0.6, ease: 'expo.out' }, 0.22)
        .add(() => action(), 0.62)
        // Um instante para a nova tela montar por baixo.
        .to({}, { duration: 0.14 })
        .to(svg, { scale: 7, opacity: 0, duration: 0.75, ease: 'expo.in' })
        .to(el, { opacity: 0, duration: 0.45, ease: 'power2.out' }, '-=0.42');
    };

    // Links internos (react-router renderiza <a href>) passam pelo portal.
    // Captura no document: roda antes do onClick do Link, que respeita defaultPrevented.
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a || a.target === '_blank' || a.hasAttribute('download') || a.dataset.noPortal !== undefined) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === pathRef.current) return; // mesma página: âncoras cuidam de si
      e.preventDefault();
      e.stopPropagation();
      api.run(() => navigate(url.pathname + url.search + url.hash), e);
    };
    document.addEventListener('click', onClick, true);

    return () => {
      document.removeEventListener('click', onClick, true);
      api.run = null;
    };
  }, [navigate]);

  return (
    <div className="portal" ref={overlay} aria-hidden>
      <svg ref={ring} className="portal-ring" viewBox="26 26 162 162" focusable="false">
        <path d={RING_PATH} fill="none" stroke="#22c55e" strokeWidth="3.4" strokeLinecap="round" />
      </svg>
    </div>
  );
}
